import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, X, FlaskConical } from "lucide-react";
import { Reveal, SectionHead } from "@/components/Reveal";
import { PROJECTS, DRAB, CANDLE, IG_GROWTH } from "@/content";

const EASE = [0.22, 1, 0.36, 1];

const PlaceholderBody = ({ project }) => (
  <>
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
      {["Strategy", "Target KPIs", "Results"].map((block) => (
        <div key={block} className="rounded-2xl border border-dashed border-terra/40 bg-secondary/40 p-5">
          <p className="mono-label mb-3">{block}</p>
          <p className="text-sm leading-relaxed text-muted-foreground">
            Full case study content coming soon — Aditi is documenting this
            campaign&apos;s {block.toLowerCase()} breakdown.
          </p>
        </div>
      ))}
    </div>
    <div className="mt-8 rounded-2xl bg-terra/10 p-5 text-center">
      <span className="font-serif text-2xl text-terra">{project.metric}</span>
      <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
        Headline metric · placeholder
      </p>
    </div>
  </>
);

const AtmabhanBody = ({ p }) => (
  <div className="space-y-9">
    <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
      {p.client.industry} · {p.client.market}
    </p>

    <div className="grid grid-cols-2 gap-4 sm:grid-cols-4" data-testid="atmabhan-stats">
      {p.stats.map((s) => (
        <div key={s.label} className="rounded-2xl border border-border/60 bg-secondary/40 p-4 text-center">
          <p className="font-serif text-2xl text-terra">{s.value}</p>
          <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.14em] text-muted-foreground">{s.label}</p>
        </div>
      ))}
    </div>

    <div>
      <p className="mono-label mb-4">Funnel strategy — attention into action</p>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {p.funnel.map((f) => (
          <div key={f.stage} className="rounded-2xl border border-border/60 bg-secondary/40 p-5">
            <p className="mono-label mb-2">{f.stage} · {f.title}</p>
            <p className="text-sm leading-relaxed text-muted-foreground">{f.body}</p>
          </div>
        ))}
      </div>
    </div>

    <div>
      <p className="mono-label mb-4">Channel plan</p>
      <div className="space-y-2.5">
        {p.channels.map((c) => (
          <div key={c.name} className="flex items-baseline gap-4 rounded-xl border border-border/50 px-4 py-3">
            <span className="w-24 shrink-0 font-semibold text-sm">{c.name}</span>
            <span className="text-sm text-muted-foreground">{c.role}</span>
          </div>
        ))}
      </div>
      <p className="mt-3 text-sm">
        <span className="font-semibold text-terra">{p.budget.total}</span>
        <span className="ml-2 font-mono text-[10px] uppercase tracking-[0.12em] text-muted-foreground">{p.budget.note}</span>
      </p>
    </div>

    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
      <div>
        <p className="mono-label mb-4">90-day proposed targets</p>
        <div className="space-y-2">
          {p.targets.map((t) => (
            <div key={t.label} className="flex items-center justify-between border-b border-border/50 pb-2 text-sm">
              <span className="text-muted-foreground">{t.label}</span>
              <span className="font-semibold text-terra">{t.value}</span>
            </div>
          ))}
        </div>
      </div>
      <div>
        <p className="mono-label mb-4">Tracking setup</p>
        <div className="flex flex-wrap gap-2">
          {p.tracking.map((t) => (
            <span key={t} className="rounded-full border border-border/70 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
              {t}
            </span>
          ))}
        </div>
        <div className="mt-6 rounded-2xl bg-terra/10 p-5">
          <p className="mono-label mb-2">Key insight</p>
          <p className="text-sm leading-relaxed">{p.insight}</p>
        </div>
      </div>
    </div>
  </div>
);

const DrabBody = () => (
  <div className="space-y-9">
    <div className="rounded-2xl border border-terra/30 bg-terra/10 p-5">
      <p className="mono-label mb-2">Objective · Meta Ads</p>
      <p className="text-sm leading-relaxed">{DRAB.objective}</p>
      <p className="mt-3 text-sm">
        <span className="font-semibold text-terra">{DRAB.budget.total}</span>
        <span className="ml-2 font-mono text-[10px] uppercase tracking-[0.12em] text-muted-foreground">
          {DRAB.budget.note}
        </span>
      </p>
    </div>

    <div data-testid="drab-mockup">
      <p className="mono-label mb-4">Campaign creative — feed, story & Facebook mockups</p>
      <div className="overflow-hidden rounded-2xl border border-border/60">
        <img
          src={DRAB.mockup}
          alt="Dr. AB campaign mockups for Instagram feed, story and Facebook"
          className="w-full object-cover"
        />
      </div>
    </div>

    <div>
      <p className="mono-label mb-4">Positioning pillars</p>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {DRAB.positioning.map((p, i) => (
          <div key={p.title} className="rounded-2xl border border-border/60 bg-secondary/40 p-5">
            <p className="mono-label mb-2">0{i + 1} · {p.title}</p>
            <p className="text-sm leading-relaxed text-muted-foreground">{p.body}</p>
          </div>
        ))}
      </div>
    </div>

    <div>
      <p className="mono-label mb-4">Campaign structure</p>
      <div className="space-y-2.5">
        {DRAB.structure.map((s) => (
          <div key={s.stage} className="flex flex-wrap items-baseline gap-x-4 gap-y-1 rounded-xl border border-border/50 px-4 py-3">
            <span className="w-32 shrink-0 text-sm font-semibold">{s.stage}</span>
            <span className="flex-1 text-sm text-muted-foreground">{s.role}</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-terra">{s.share}</span>
          </div>
        ))}
      </div>
    </div>

    <div>
      <p className="mono-label mb-4">Ad angles & messaging</p>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {DRAB.ads.map((a) => (
          <div key={a.angle} className="rounded-2xl border border-border/60 bg-secondary/40 p-5">
            <p className="mono-label mb-2">{a.angle}</p>
            <p className="text-sm leading-relaxed">&ldquo;{a.message}&rdquo;</p>
            <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.12em] text-terra">CTA · {a.cta}</p>
          </div>
        ))}
      </div>
    </div>

    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
      <div>
        <p className="mono-label mb-4">Audience & targeting</p>
        <ul className="space-y-2">
          {DRAB.audience.map((a) => (
            <li key={a} className="text-sm leading-relaxed text-muted-foreground">— {a}</li>
          ))}
        </ul>
      </div>
      <div>
        <p className="mono-label mb-4">Measurement</p>
        <div className="space-y-2">
          {DRAB.measurement.map((m) => (
            <div key={m.label} className="flex items-baseline justify-between gap-4 border-b border-border/50 pb-2 text-sm">
              <span className="font-medium">{m.label}</span>
              <span className="text-right text-xs text-muted-foreground">{m.note}</span>
            </div>
          ))}
        </div>
        <div className="mt-6 rounded-2xl bg-terra/10 p-5">
          <p className="mono-label mb-2">Success measure</p>
          <p className="text-sm leading-relaxed">{DRAB.success}</p>
        </div>
      </div>
    </div>
  </div>
);

const PriorityPill = ({ p }) => (
  <span
    className={`rounded-full px-2.5 py-0.5 font-mono text-[9px] uppercase tracking-wider ${
      p === "High" ? "bg-terra/15 text-terra" : "bg-secondary text-muted-foreground"
    }`}
  >
    {p}
  </span>
);

const CandleBody = () => (
  <div className="space-y-9">
    <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
      Candles · Home Fragrance · Lifestyle — {CANDLE.scope}
    </p>

    <div>
      <p className="mono-label mb-4">Audit findings</p>
      <div className="space-y-2.5">
        {CANDLE.findings.map((f) => (
          <div key={f.param} className="flex flex-wrap items-baseline gap-x-4 gap-y-1 rounded-xl border border-border/50 px-4 py-3">
            <span className="w-44 shrink-0 text-sm font-semibold">{f.param}</span>
            <span className="flex-1 text-sm text-muted-foreground">{f.note}</span>
            <PriorityPill p={f.priority} />
          </div>
        ))}
      </div>
    </div>

    <div>
      <p className="mono-label mb-4">Keyword research & search intent</p>
      <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
        {CANDLE.keywords.map((k) => (
          <div key={k.kw} className="flex items-center justify-between gap-3 rounded-xl border border-border/50 px-4 py-2.5">
            <div>
              <p className="text-sm font-medium">{k.kw}</p>
              <p className="font-mono text-[9px] uppercase tracking-[0.12em] text-muted-foreground">{k.intent}</p>
            </div>
            <PriorityPill p={k.priority} />
          </div>
        ))}
      </div>
    </div>

    <div>
      <p className="mono-label mb-4">Keyword-to-page mapping</p>
      <div className="space-y-2.5">
        {CANDLE.mapping.map((m) => (
          <div key={m.search} className="flex flex-wrap items-baseline gap-x-4 gap-y-1 rounded-xl border border-border/50 px-4 py-3">
            <span className="flex-1 text-sm font-medium">{m.search}</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-terra">{m.dest}</span>
          </div>
        ))}
      </div>
    </div>

    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
      <div className="rounded-2xl border border-terra/30 bg-terra/10 p-5">
        <p className="mono-label mb-3">On-page recommendations</p>
        <p className="text-sm"><span className="text-muted-foreground">Title:</span> {CANDLE.onpage.title}</p>
        <p className="mt-2 text-sm"><span className="text-muted-foreground">Meta:</span> {CANDLE.onpage.meta}</p>
        <p className="mt-2 text-sm"><span className="text-muted-foreground">H1:</span> {CANDLE.onpage.h1}</p>
      </div>
      <div>
        <p className="mono-label mb-3">Technical SEO</p>
        <div className="space-y-2">
          {CANDLE.technical.map((t) => (
            <div key={t.item} className="text-sm">
              <span className="font-semibold">{t.item}:</span>{" "}
              <span className="text-muted-foreground">{t.details}</span>
            </div>
          ))}
        </div>
      </div>
    </div>

    <div>
      <p className="mono-label mb-4">30-day action plan</p>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
        {CANDLE.plan.map((w) => (
          <div key={w.week} className="rounded-2xl border border-border/60 bg-secondary/40 p-4">
            <p className="mono-label mb-2">{w.week}</p>
            <p className="text-sm leading-relaxed text-muted-foreground">{w.actions}</p>
          </div>
        ))}
      </div>
      <div className="mt-6 rounded-2xl bg-terra/10 p-5">
        <p className="mono-label mb-2">Conclusion</p>
        <p className="text-sm leading-relaxed">{CANDLE.conclusion}</p>
      </div>
    </div>
  </div>
);

const IgGrowthBody = () => (
  <div className="space-y-8">
    <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
      {IG_GROWTH.period} · 3 client accounts
    </p>
    {IG_GROWTH.clients.map((c, i) => (
      <div
        key={c.handle}
        data-testid={`ig-client-${i + 1}`}
        className="overflow-hidden rounded-2xl border border-border/60"
      >
        <img src={c.image} alt={`Instagram growth infographic for ${c.name}`} className="w-full object-cover" />
        <div className="flex flex-wrap items-center justify-between gap-3 bg-secondary/40 px-5 py-4">
          <div>
            <p className="font-semibold">{c.name} <span className="font-mono text-xs text-muted-foreground">{c.handle}</span></p>
            <p className="text-xs text-muted-foreground">{c.role}</p>
          </div>
          <div className="text-right">
            <p className="font-serif text-xl text-terra">{c.from} → {c.to} · {c.growth}</p>
            <p className="text-xs text-muted-foreground">{c.insight}</p>
          </div>
        </div>
      </div>
    ))}
  </div>
);

const CaseStudyModal = ({ project, onClose }) => (
  <AnimatePresence>
    {project && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] flex items-end justify-center bg-background/70 backdrop-blur-sm sm:items-center sm:p-6"
        onClick={onClose}
        data-testid="project-modal-overlay"
      >
        <motion.div
          initial={{ y: 60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 60, opacity: 0 }}
          transition={{ duration: 0.45, ease: EASE }}
          onClick={(e) => e.stopPropagation()}
          data-lenis-prevent
          className="max-h-[85vh] w-full max-w-3xl overflow-y-auto overscroll-contain rounded-t-[2rem] border border-border/60 bg-card p-8 shadow-2xl sm:rounded-[2rem] sm:p-10"
          data-testid="project-modal"
        >
          <div className="mb-6 flex items-start justify-between gap-4">
            <div>
              <p className="mono-label mb-2 flex items-center gap-2">
                <FlaskConical size={13} />
                {project.tag}
              </p>
              <h3 className="font-serif text-2xl tracking-tight sm:text-3xl">{project.title}</h3>
              {project.subtitle && (
                <p className="mt-1 font-serif italic text-muted-foreground">{project.subtitle}</p>
              )}
            </div>
            <button
              data-testid="project-modal-close"
              onClick={onClose}
              aria-label="Close case study"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-border/60 text-muted-foreground transition-colors hover:text-terra"
            >
              <X size={16} />
            </button>
          </div>

          <p className="mb-8 leading-relaxed text-muted-foreground">{project.description}</p>

          {project.id === "atmabhan" ? (
            <AtmabhanBody p={project} />
          ) : project.id === "drab" ? (
            <DrabBody />
          ) : project.id === "candle" ? (
            <CandleBody />
          ) : project.id === "iggrowth" ? (
            <IgGrowthBody />
          ) : (
            <PlaceholderBody project={project} />
          )}
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

export const Projects = () => {
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    document.documentElement.classList.toggle("lenis-stopped", !!selected);
    document.body.style.overflow = selected ? "hidden" : "";
    return () => {
      document.documentElement.classList.remove("lenis-stopped");
      document.body.style.overflow = "";
    };
  }, [selected]);

  return (
    <section
      id="projects"
      data-testid="projects-section"
      className="scroll-mt-24 px-6 py-24 sm:px-12 lg:px-20 lg:py-32"
    >
      <div className="mx-auto max-w-7xl">
        <SectionHead
          label="02 — Projects"
          title="Case studies with real numbers behind them."
          testId="projects-heading"
        />

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-12 lg:gap-8" data-testid="projects-grid">
          {PROJECTS.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.08} className={`md:col-span-1 ${p.span}`}>
              <button
                data-testid={`project-card-${i + 1}`}
                onClick={() => setSelected(p)}
                className="group relative block w-full overflow-hidden rounded-[1.75rem] border border-border/60 text-left shadow-lg transition-transform duration-500 hover:-translate-y-1.5"
              >
                <div className="relative h-72 overflow-hidden sm:h-80">
                  <img
                    src={p.image}
                    alt={p.title}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.06]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-black/10" />
                </div>

                <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8">
                  <p className={`mb-2 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] ${p.placeholder ? "text-honey" : "text-terra"}`}>
                    <FlaskConical size={12} />
                    {p.tag}
                  </p>
                  <div className="flex items-end justify-between gap-4">
                    <div>
                      <h3 className="font-serif text-2xl tracking-tight text-[#FAF7F2] sm:text-3xl">
                        {p.title}
                      </h3>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {p.tags.map((t) => (
                          <span
                            key={t}
                            className="rounded-full border border-white/25 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-white/85"
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                    </div>
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-terra text-primary-foreground transition-transform duration-300 group-hover:rotate-45">
                      <ArrowUpRight size={18} />
                    </span>
                  </div>
                </div>

                <span className="absolute right-5 top-5 rounded-full bg-background/85 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-foreground backdrop-blur-md">
                  {p.metric}
                </span>
              </button>
            </Reveal>
          ))}
        </div>

      </div>

      <CaseStudyModal project={selected} onClose={() => setSelected(null)} />
    </section>
  );
};
